import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Callout, Card, EmptyState, Field, SectionHeading, Select, type ToneName } from '@/components/ui';
import { formatDate, todayIso } from '@/domain/dates';
import { formatEur } from '@/domain/money';
import { DEFAULT_NUMBER_CIRCLE, findSequenceGaps } from '@/domain/invoiceNumber';
import type { Invoice } from '@/domain/types';
import { invoiceRepo } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { useT } from '@/i18n';
import { isOverdue } from './invoiceService';

const STATUS_TONES: Record<Invoice['status'], ToneName> = {
  entwurf: 'neutral',
  offen: 'info',
  bezahlt: 'success',
  storniert: 'neutral',
  ueberfaellig: 'danger',
};

export function InvoiceListScreen() {
  const session = useSession();
  const t = useT();
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [filter, setFilter] = useState<'alle' | Invoice['status']>('alle');

  useEffect(() => {
    void (async () => {
      const all = await invoiceRepo.all(session.dataKey);
      setInvoices(
        all.sort((a, b) =>
          b.issueDate.localeCompare(a.issueDate) || b.invoiceNumber.localeCompare(a.invoiceNumber),
        ),
      );
    })();
  }, [session.dataKey]);

  const gaps = useMemo(() => {
    if (!invoices) return [];
    const byCircle = new Map<string, string[]>();
    for (const invoice of invoices) {
      if (!invoice.invoiceNumber) continue;
      const list = byCircle.get(invoice.numberCircle);
      if (list) list.push(invoice.invoiceNumber);
      else byCircle.set(invoice.numberCircle, [invoice.invoiceNumber]);
    }
    return findSequenceGaps(DEFAULT_NUMBER_CIRCLE, byCircle);
  }, [invoices]);

  const filtered = invoices?.filter((invoice) => filter === 'alle' || invoice.status === filter);

  const outstanding =
    invoices
      ?.filter((invoice) => invoice.status === 'offen' || invoice.status === 'ueberfaellig')
      .reduce((sum, invoice) => sum + invoice.grossTotalCents, 0) ?? 0;

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.invoices.title}
        description={outstanding > 0 ? t.invoices.outstanding(formatEur(outstanding)) : undefined}
        action={
          <Link to="/rechnungen/neu" className="btn-primary">
            {t.invoices.create}
          </Link>
        }
      />

      {gaps.length > 0 && (
        <Callout tone="danger" title={t.invoices.gapTitle}>
          {gaps
            .map((gap) => t.invoices.gapEntry(gap.circle, gap.missing.join(', ')))
            .join(' · ')}
          {t.invoices.gapNote}
        </Callout>
      )}

      <Card>
        <Field label={t.common.status}>
          <Select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
            <option value="alle">{t.common.all}</option>
            {Object.entries(t.labels.invoiceStatus).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </Card>

      {!filtered ? (
        <p className="text-muted text-sm">{t.common.loading}</p>
      ) : filtered.length === 0 ? (
        <EmptyState title={t.invoices.empty}>
          <Link to="/rechnungen/neu" className="text-accent underline">
            {t.invoices.firstInvoice}
          </Link>
        </EmptyState>
      ) : (
        <ul className="space-y-2">
          {filtered.map((invoice) => {
            const overdue = isOverdue(invoice, todayIso());
            return (
              <li key={invoice.id}>
                <Link
                  to={`/rechnungen/${invoice.id}`}
                  className="card hover:border-line-strong block transition-colors"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-heading text-sm font-medium">
                        {invoice.invoiceNumber || t.invoices.draft}
                      </p>
                      <p className="text-muted mt-0.5 truncate text-xs">
                        {invoice.customerName || t.invoices.noCustomer} ·{' '}
                        {formatDate(invoice.issueDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-heading text-sm font-semibold">
                        {formatEur(invoice.grossTotalCents)}
                      </p>
                      <p className="text-muted text-xs">
                        {t.invoices.positions(invoice.lines.length)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge tone={overdue ? 'danger' : STATUS_TONES[invoice.status]}>
                      {overdue && invoice.status !== 'ueberfaellig'
                        ? t.invoices.overdueBadge
                        : t.labels.invoiceStatus[invoice.status]}
                    </Badge>
                    {invoice.isKleinunternehmer && <Badge>§ 19 UStG</Badge>}
                    {invoice.isKleinbetrag && invoice.status !== 'entwurf' && (
                      <Badge>{t.invoices.kleinbetrag}</Badge>
                    )}
                    {invoice.dunningLevel > 0 && (
                      <Badge tone="warning">{t.invoices.dunningBadge(invoice.dunningLevel)}</Badge>
                    )}
                    {invoice.eInvoiceGeneratedAt && <Badge tone="info">{t.invoices.eInvoice}</Badge>}
                    {invoice.status !== 'entwurf' && (
                      <span className="text-muted text-xs">
                        {t.invoices.dueOn(formatDate(invoice.dueDate))}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
